import { HttpClient } from '@/core/http/http-client';
import { User } from '@/modules/Users/domain';
import { mapUsers } from '@/modules/Users/mappers';
import { UserJson } from '@/modules/Users/types';
import { SortDirection } from '@/shared';
import { Request, RequestDetails, RequestKpi, RequestTotals } from '../domain';
import {
  mapRequestActivities,
  mapRequestDetails,
  mapRequests,
  mapRequestsKpis,
  mapRequestTotals,
} from '../mappers';
import {
  ApiResponse,
  ApiResponseRequest,
  ApiResponseRequestClaimants,
  ApiResponseRequests,
  ApiResponseRequestTotals,
  RequestActivityJson,
  RequestDetailsJson,
  RequestJson,
  RequestKpiJson,
  RequestsFiltersReturn,
  RequestsKpiDetailsFiltersReturn,
  RequestTotalsJson,
} from '../types';

export class RequestRepository {
  constructor(private readonly client: HttpClient) {}

  async getTotals(onlyActiveRequests: boolean): Promise<RequestTotals> {
    const response = await this.client.get<
      ApiResponseRequestTotals<RequestTotalsJson>
    >({
      url: `totals`,
      query: { onlyActiveRequests },
    });

    return mapRequestTotals(response?.data.totals);
  }

  async getDetails(requestId: number): Promise<RequestDetails> {
    const response = await this.client.get<
      ApiResponseRequest<RequestDetailsJson>
    >({
      url: `me/requests/${requestId}`,
    });

    return mapRequestDetails(response.data.request);
  }

  async getRequests(
    filters: RequestsFiltersReturn,
    sortBy?: string,
    sortOrder?: SortDirection,
    pageNumber?: number,
    rowsPerPage?: number,
  ): Promise<Request[]> {
    const response = await this.client.get<ApiResponseRequests<RequestJson[]>>({
      url: 'me/requests',
      query: {
        ...filters,
        sortBy,
        sortOrder,
        pageNumber,
        rowsPerPage,
      },
    });

    return mapRequests(response.data.requests);
  }

  async getRequestsClaimants(): Promise<User[]> {
    const response = await this.client.get<
      ApiResponseRequestClaimants<UserJson[]>
    >({
      url: 'claimants',
    });

    return mapUsers(response.data.claimants);
  }

  async getRequestsKpiDetails(
    filters: RequestsKpiDetailsFiltersReturn,
    sortBy?: string,
    sortOrder?: SortDirection,
    pageNumber?: number,
    rowsPerPage: number = 10,
  ): Promise<RequestKpi[]> {
    const response = await this.client.get<
      ApiResponseRequests<RequestKpiJson[]>
    >({
      url: 'me/requests-kpis',
      query: {
        ...filters,
        sortBy,
        sortOrder,
        pageNumber,
        rowsPerPage,
      },
    });

    return mapRequestsKpis(response.data.requests);
  }

  async getRequestActivities(id: number) {
    const response = await this.client.get<
      ApiResponse<{ activities: RequestActivityJson[] }>
    >({
      url: `me/${id}/activities`,
    });

    return mapRequestActivities(response.data.activities ?? []);
  }
}
